import React, { createContext, useState, useContext, useRef, useEffect } from 'react';
import { useSubscription } from './SubscriptionContext';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';

// Types
interface SessionContextType {
  isSessionActive: boolean;
  sessionTime: number;
  customApiKey: string;
  startSession: () => void;
  stopSession: () => void;
  setCustomApiKey: (key: string) => void;
}

// Create context
const SessionContext = createContext<SessionContextType>({
  isSessionActive: false,
  sessionTime: 0,
  customApiKey: '',
  startSession: () => {},
  stopSession: () => {},
  setCustomApiKey: () => {},
});

// Custom hook
export const useSession = () => useContext(SessionContext);

// Provider component
export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { remainingMinutes, useMinutes } = useSubscription();
  
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [sessionTime, setSessionTime] = useState(0);
  const [customApiKey, setCustomApiKey] = useState('');
  
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const currentSessionIdRef = useRef<string | null>(null);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Update timer while session is active
  useEffect(() => {
    if (isSessionActive) {
      startTimeRef.current = Date.now();
      
      timerRef.current = setInterval(() => {
        if (startTimeRef.current) {
          const elapsedSeconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
          setSessionTime(elapsedSeconds);
          
          // Check if we need to stop the session due to time limit
          if (!customApiKey && elapsedSeconds >= remainingMinutes * 60) {
            stopSession();
          }
        }
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isSessionActive, remainingMinutes, customApiKey]);

  const startSession = () => {
    // Prevent concurrent sessions
    if (isSessionActive) return;
    
    // Ensure user is authenticated
    if (!user) {
      console.error('Cannot start session: user not authenticated');
      return;
    }
    
    setIsSessionActive(true);
    setSessionTime(0);
    
    // Log session start
    logSessionStart();
  };

  const logSessionStart = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('sessions')
        .insert({
          user_id: user.id,
          type: 'both',
          used_byok: !!customApiKey,
          start_time: new Date().toISOString(),
        })
        .select('id')
        .single();
      
      if (error) {
        console.error('Failed to log session start:', error);
      } else if (data) {
        currentSessionIdRef.current = data.id;
        console.log('Session started with ID:', data.id);
      }
    } catch (error) {
      console.error('Failed to log session:', error);
    }
  };

  const stopSession = () => {
    if (!isSessionActive) return;
    
    if (isSessionActive && startTimeRef.current) {
      const elapsedMinutes = Math.ceil((Date.now() - startTimeRef.current) / (1000 * 60));
      const elapsedSeconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
      
      // If user is using their own API key, don't deduct from their remaining minutes
      if (!customApiKey) {
        useMinutes(elapsedMinutes);
      }
      
      // Log session end
      logSessionEnd(elapsedSeconds);
    }
    
    setIsSessionActive(false);
    startTimeRef.current = null;
    currentSessionIdRef.current = null;
  };

  const logSessionEnd = async (duration: number) => {
    if (!user || !currentSessionIdRef.current) return;
    
    try {
      const { error } = await supabase
        .from('sessions')
        .update({
          end_time: new Date().toISOString(),
          duration: duration,
        })
        .eq('id', currentSessionIdRef.current)
        .eq('user_id', user.id);
      
      if (error) {
        console.error('Failed to log session end:', error);
      } else {
        console.log('Session ended, duration:', duration, 'seconds');
      }
    } catch (error) {
      console.error('Failed to update session:', error);
    }
  };

  const value = {
    isSessionActive,
    sessionTime,
    customApiKey,
    startSession,
    stopSession,
    setCustomApiKey,
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};